/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState } from 'react'
import { Button, Flex, Text, rem, Image, Tooltip } from '@mantine/core'
import { Gen360toHDR, PipelineParameters } from '../pipeline/Pipeline'
import { useStore } from '../../../hooks/use-store'
import { IconX } from '@tabler/icons-react'
import { observer } from 'mobx-react'
import { priceMap } from '../pipeline/Router'
import { UploadCarPerspectiveButton } from '../pipeline/UploadCarPerspectiveButton'
import { useHistory } from 'react-router-dom'
import { UploadCarHDRButton } from '../pipeline/UploadCarHDRButton'

function Element(model: {
  buy: (params: PipelineParameters[]) => Promise<void>;
  loading: boolean
}) {
  const history = useHistory()
  const {
    dataStore: { spaceStore },
    uiStore: { authStore },
  } = useStore();

  const [modelParams, setModelParams] = useState({
    brightness_boost: 0.8,
    save_format: 'hdr',
  });

  const handleRemoveImage = () => {
    spaceStore.setSelectedImage({ src: null });
  };

  return (
    <Flex w={'100%'} mt={12} pt={32} pb={32} pl={40} pr={40} style={{ borderRadius: '4px', background: 'rgba(32, 32, 32, 1)' }} gap={32} pos='relative'>
      <Flex pos='absolute' top='0' right='0' style={{ cursor: 'pointer' }} onClick={() => {
        history.push('/history')
      }}>
        <svg width="40" height="40" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.17157 11.2944L10.8284 5.63758" stroke="#BDBDBD" stroke-linecap="round" stroke-linejoin="round"></path><path d="M10.8284 11.2945L5.17157 5.6377" stroke="#BDBDBD" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
      </Flex>
      <Flex direction={'column'} miw={'480px'} w={'480px'} gap={12}>

        <Text fz={32} fw={800} color='#fff' lh={1.2} mb={12}>Generate 360° HDR Domes

        </Text>
        <Text fz={16} color='#fff' lh={1.4} maw={440} mb={12}>
          Create lighting environments that perfectly match your visuals.<Tooltip
            label={
              <Text fw={500}>
                Generate high-dynamic-range domes that match your generated images, or even upload your own image and let the AI generate a fitting HDR Dome.
                The result is a 360° HDR Dome ideal for realistic lighting and reflections in your external CGI workflow.
              </Text>
            }
            multiline
            w={320}
            withArrow
            position="bottom"
            color='dark'
          >
            <span style={{ marginLeft: 4, cursor: 'pointer', display: 'inline-block' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="#4badf4" class="icon icon-tabler icons-tabler-filled icon-tabler-info-square-rounded"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 2l.642 .005l.616 .017l.299 .013l.579 .034l.553 .046c4.687 .455 6.65 2.333 7.166 6.906l.03 .29l.046 .553l.041 .727l.006 .15l.017 .617l.005 .642l-.005 .642l-.017 .616l-.013 .299l-.034 .579l-.046 .553c-.455 4.687 -2.333 6.65 -6.906 7.166l-.29 .03l-.553 .046l-.727 .041l-.15 .006l-.617 .017l-.642 .005l-.642 -.005l-.616 -.017l-.299 -.013l-.579 -.034l-.553 -.046c-4.687 -.455 -6.65 -2.333 -7.166 -6.906l-.03 -.29l-.046 -.553l-.041 -.727l-.006 -.15l-.017 -.617l-.004 -.318v-.648l.004 -.318l.017 -.616l.013 -.299l.034 -.579l.046 -.553c.455 -4.687 2.333 -6.65 6.906 -7.166l.29 -.03l.553 -.046l.727 -.041l.15 -.006l.617 -.017c.21 -.003 .424 -.005 .642 -.005zm0 9h-1l-.117 .007a1 1 0 0 0 0 1.986l.117 .007v3l.007 .117a1 1 0 0 0 .876 .876l.117 .007h1l.117 -.007a1 1 0 0 0 .876 -.876l.007 -.117l-.007 -.117a1 1 0 0 0 -.764 -.857l-.112 -.02l-.117 -.006v-3l-.007 -.117a1 1 0 0 0 -.876 -.876l-.117 -.007zm.01 -3l-.127 .007a1 1 0 0 0 0 1.986l.117 .007l.127 -.007a1 1 0 0 0 0 -1.986l-.117 -.007z" /></svg>
            </span>
          </Tooltip>
        </Text>

        {
          authStore.isProAndGreater() ? <UploadCarHDRButton
            text={spaceStore.selectedImage ? "Re-upload image" : "Input image"}
            onSetImage={(image) => {
              spaceStore.setSelectedImage({
                src: image
              })
            }} />
            : <Button
              styles={{
                root: {
                  color: 'rgba(230, 230, 230, 1)',
                  height: rem(36),
                  fontSize: '14px',
                  background: '#f53c02 !important',
                  border: 'none',
                  position: 'relative',
                }
              }}
              variant="default"
              onClick={async () => {
                spaceStore.setBillingAccessModalOpen(true)
              }}
            >
              Upload custom image (PRO)
            </Button>
        }

        <Flex gap={12} mt={32}>
          <Tooltip
            p={12}
            label={
              <span>Generation cost:
                &nbsp;
                <span style={{
                  color: '#f53c02',
                  fontWeight: 700
                }}>{priceMap.SHUTTER_IMAGE_HDR} credits
                </span>
              </span>
            }
            withArrow
            position="right"
            color='dark'
          >
            <Button
              styles={{
                root: {
                  color: 'rgba(230, 230, 230, 1)',
                  height: rem(36),
                  fontSize: '14px',
                  background: '#f53c02 !important',
                  border: 'none',
                  position: 'relative',
                }
              }}
              disabled={!spaceStore.selectedImage}
              variant="default"
              onClick={async () => {
                const pipeline: Gen360toHDR = {
                  provider: 'custom',
                  type: 'genHDR2',
                  inputSrc: spaceStore.selectedImage!,
                  brightness_boost: modelParams.brightness_boost,
                  save_format: modelParams.save_format,
                  srcset: [],
                  credits: priceMap.SHUTTER_IMAGE_HDR,
                }
                await model.buy([pipeline]);
              }}
            >
              Generate 360° HDR
            </Button>
          </Tooltip>
        </Flex>
      </Flex>

      {spaceStore.selectedImage && (
        <Flex direction="column" gap={8} style={{ flex: 1 }}>
          <Flex justify="space-between" align="center">
            <Text fz={20}>Preview</Text>
            <Button
              variant="subtle"
              color="red"
              size="xs"
              leftIcon={<IconX size={16} />}
              onClick={handleRemoveImage}
            >
              Remove
            </Button>
          </Flex>
          <Image
            src={spaceStore.selectedImage}
            alt="Selected panorama"
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '600px',
              objectFit: 'contain',
              borderRadius: '4px'
            }}
          />
        </Flex>
      )}
    </Flex>
  )
}

export const Shutter360toHDRImageScreen = observer(Element);
